import { useEffect } from 'react';

import { readAppDisplay } from './client-type.js';
import { wantsEntryFullscreen } from './entry-fullscreen.js';

/**
 * 첫 조작에서 창을 전체화면으로 (2026-09-16). **얇은 어댑터다** — 판정은
 * `entry-fullscreen.ts` 가 갖고 여기는 읽고 부를 뿐이며, `use-channel-keys.ts` 와 같은
 * 지위라 **테스트하지 않는다.**
 *
 * ── 왜 화면이 아니라 여기인가 ────────────────────────────────
 *
 * 재생을 여는 문은 하나가 아니다 — 홈의 OK · 채널 카드 · 편성표 카드 · 숫자 키 · 옛
 * 게이트 · ONAIR. 그 여섯에 같은 줄을 적으면 **일곱 번째 문이 생기는 날 조용히 빠진다**
 * (절대 규칙 29 가 셀렉터 목록을 금지하는 그 이유). 여기서는 **문을 열거하지 않고**
 * 문서의 첫 조작 하나를 듣는다 — `useTvFocus`·`useBrowserBack`·`useTouchInput` 이
 * `App()` 최상위에 서 있는 것과 같은 모양이다.
 *
 * ── 한 문서에 한 번이다 ──────────────────────────────────────
 *
 * 성공이든 거절이든 리스너를 걷는다. **다시 걸지 않는 것이 요점이다** — 시청자가 ESC 로
 * 전체화면을 나간 것은 **나가려던 것**이고, 다음 클릭에 다시 끌어들이면 앱이 사람과
 * 싸운다. 되돌아가는 길은 시청자의 ⌃⌘F 하나로 남긴다.
 *
 * ── ★★ 재생보다 늦게 요청한다 — 그렇지 않으면 **소리를 빼앗는다** ──
 *
 * `requestFullscreen()` 은 **일시적 사용자 활성화를 소비한다.** 진입 제스처가 하는 일이
 * 정확히 그 활성화로 소리 있는 재생을 세우는 것이고(`unlockBuffers()` → 새 화면의
 * `start()` 는 `useEffect` 라 **제스처 밖**이다 · `HomeScreen` 의 그 주석), 같은 클릭에서
 * 곧바로 전체화면을 부르면 **뒤에 오는 `play()` 가 거절된다.**
 *
 * 실측으로 걸렸다(2026-09-16) — 자동재생이 막힌 창에서 `verify-autostart` 의
 * `refusalFallsBackToGate` 가 빨개졌다: 게이트를 눌렀는데 **`currentTime: 0` 이고 게이트가
 * 그대로**였다(고치기 전 같은 실행에서 4.85). 화면에는 「전체화면은 됐는데 영상이 안 나온다」로
 * 보이고, 그것이 절대 규칙 72 의 자물쇠 ②가 지키는 바로 그 경로다.
 *
 * 그래서 **활성화 창(약 5초) 안에서, 재생이 선 뒤에** 부른다. `0` 으로 두지 않는 이유는
 * 위와 같고(같은 태스크면 소비 순서가 그대로다), 5초에 붙이지 않는 이유는 그 창이 꺼지면
 * 조용히 거절되기 때문이다. `Page` 전환·매니페스트 적재가 끝나기에 넉넉하고 사람 눈에는
 * **OK 한 번에 재생이 먼저 서고 창이 뒤따르는** 순서로 보인다.
 *
 * ── 거품 단계에서 듣는다 ─────────────────────────────────────
 *
 * `capture` 가 아니다(`use-tv-focus.ts` 와 반대다). 화면의 핸들러가 **먼저** 끝나야
 * 하기 때문이다 — 위의 소비 순서가 첫째 이유이고, 전체화면 전환이 macOS 에서 화면 전체가
 * 움직이는 애니메이션이라 그 한가운데에서 React 가 홈을 걷게 하고 싶지 않은 것이 둘째다.
 *
 * ── ★ 대가: 그 안에서 ESC 는 브라우저의 것이다 (실측 2026-09-16) ──
 *
 * 전체화면 API 로 들어간 문서에서 **ESC 는 전체화면을 나가고, `keydown` 이 페이지에
 * 오지 않는다**(계수기 0). ⌃⌘F 로 띄운 창은 반대다 — **ESC 가 페이지에 오고 전체화면은
 * 유지된다**(계수기 1). 그래서 이 기능을 켜는 동안 **ESC 로 층을 닫는 길이 사라진다**
 * (`keys.ts` 의 `Escape → back`).
 *
 * **잃는 것이 그것 하나이고 대체 수단이 둘이다** — 층마다 있는 닫기 버튼
 * (`data-focus-back` · 리모컨의 「뒤로」가 누르는 그 버튼)과 TV 의 뒤로 키
 * (`XF86Back`·`BrowserBack`·461·10009 — 브라우저가 예약하지 않는다). 노트북 키보드에
 * 뒤로 키를 하나 더 두는 것(예: `Backspace`)은 **리모컨 계약을 넓히는 일**이라
 * 여기서 혼자 결정하지 않는다.
 *
 * `keydown` 도 함께 듣는 이유는 **리모컨으로만 조작하는 경로**다(숫자 키·Enter).
 *
 * ── ★★ 거절은 포기가 아니다 (2026-09-16 · 두 번째 판) ────────
 *
 * 첫 판은 **한 번 시도하고 무조건 걷었다.** 그러면 그 한 번이 어떤 이유로든 거절됐을 때
 * (활성화가 안 붙는 키 · 창이 분할 화면·스테이지 매니저에 있음 · 우리가 모르는 정책)
 * **그 문서에서는 영영 전체화면이 되지 않고, 화면에는 「안 된다」 하나만 남는다.**
 * 실제로 「여전히 전체화면이 아니다」라는 보고를 받았고, 재현되는 경로를 셋(클릭·화살표·
 * Enter) 다 밟아도 배포판에서는 붙었다 — **재현되지 않는 거절이 있다는 뜻**이라, 한 번에
 * 포기하는 설계 자체를 고쳤다.
 *
 * 그래서 **성공할 때까지 조작마다 다시 해 보고, 성공하면 그때 걷는다.** 걷는 조건이
 * 「한 번 해 봤다」에서 「한 번 됐다」로 바뀐 것이고, **사람과 싸우지 않는다**는 성질은
 * 그대로다 — 성공 뒤에는 리스너가 없으므로 시청자가 ESC 로 나간 뒤 다시 끌어들이지 않는다.
 *
 * ── ★★★ 제스처 없이 들어가는 길은 **권한 하나**다 (실측 2026-09-16) ──
 *
 * 「앱 아이콘을 더블클릭하면 그 순간 전체화면이면 좋겠다」에는 **웹 표준만으로는 답이
 * 없다.** 그런데 크롬에는 사이트별 **「자동 전체 화면」 콘텐츠 설정**이 있고
 * (`chrome://settings/content/automaticFullScreen`), 그것이 켜진 사이트에서는
 * `requestFullscreen()` 이 **활성화 없이도 성립한다.** 같은 앱 창에서 잰 값:
 *
 * | 창 | 제스처 없는 요청 |
 * |---|---|
 * | 그냥 앱 창 | `TypeError: Permissions check failed` |
 * | **그 권한을 준 사이트** | **`granted`** — 조작 0회로 전체화면 |
 *
 * 그래서 **뜨자마자 한 번 물어본다.** 권한이 있으면 그대로 들어가고, 없으면 거절이
 * 돌아오는데 그때 **아무 일도 일어나지 않는다** — 첫 조작을 기다리는 아래 길이 그대로
 * 남는다. 즉 **이 요청은 권한을 켠 사람에게만 보이고, 켜지 않은 사람에게는 없는 것과
 * 같다.** 권한을 코드가 요구하지 않는 것이 요점이다(요구하면 켜지 않은 창이 고장난다).
 *
 * ★ **한 번만 물으면 안 된다** (실측 2026-09-16). 전체화면인 창에서 문서가 다시 서면
 * (`⌘R` · 앱 안 이동) macOS 는 창을 **전체화면에서 빼내는 애니메이션**을 돌리는데, 그
 * 한가운데에 마운트가 걸리면 권한이 있어도 **거절된다.** 크롬이 그때 주는 말은
 * `TypeError: not granted` 이고, **그 상태가 5초쯤 간다** — 같은 창에서 재 보면 +1.2·2.4·
 * 3.9초는 `not granted` 이고 +5.9초에 `granted` 였다(2026-09-16 실측). 그래서 거절되면
 * **그 너머까지 몇 번 더 물어본다**(`AUTO_RETRY_MS` — 마지막이 6초다). 권한이 없는
 * 창에서는 그 넷이 전부 조용히 거절될 뿐이라 대가가 없다.
 *
 * **대가 하나** — 권한을 켜면 문서가 설 때마다 전체화면으로 들어간다. 시청자가 ESC 로
 * 나가도 **다음 적재에서 다시** 들어간다(그 설정의 뜻이 그것이다). 그리고 그 창에서는
 * `display-mode` 가 곧 `fullscreen` 이 되므로 `autoStartsWithoutGesture` 가 거짓이 되어
 * **옛 진입 게이트를 쓰는 경로(`?debug=1`)에서는 버튼이 한 번 더 뜬다** — 홈이 진입
 * 표면인 보통 경로(절대 규칙 81)에는 영향이 없다.
 *
 * ── 판정을 문서 루트에 적는다 (`data-entry-fullscreen`) ──────
 *
 * `auto-start.ts` 의 `data-auto-start` 와 **같은 이유, 같은 방침**이다: 이 기능의 실패는
 * 조용하고, 화면에서는 **「구현이 안 된 것」과 완전히 같다.** 무대에서 개발자 도구 한 줄로
 * 이유가 나오는 축을 남긴다(설치된 앱에는 주소창이 없어 `?debug=1` 을 쓸 수 없다).
 *
 * | 값 | 뜻 |
 * |---|---|
 * | `off` | 여기서 켜지 않는다 — **브라우저 탭**이거나 손으로 쓰는 기기다 |
 * | `already` | 이미 전체화면이다(⌃⌘F 로 띄운 창 · 전체화면 API 안) |
 * | `armed` | 리스너가 붙었다 — **첫 조작을 기다리는 중** |
 * | `requested` | 제스처를 받아 요청을 걸었다(지연 중) |
 * | `on` | 들어갔다. 리스너는 걷혔다 |
 * | `denied` | 거절됐다 — **다음 조작에서 다시 해 본다** |
 */
/**
 * 제스처에서 전체화면 요청까지의 지연(ms). **일시적 사용자 활성화가 사는 동안**(크롬 약 5초)
 * 이어야 하고, 그 안에서 **재생이 먼저 서기에 넉넉해야** 한다 — 위 ★★.
 */
const ENTER_DELAY_MS = 1200;

/**
 * 제스처 **없는** 시도의 간격(ms) — 첫 번째는 즉시. 나머지 둘은 위 ★ 의 그 전환 때문이고,
 * **권한이 없는 창에서는 셋 다 조용히 거절된다**(그래서 이 값들이 아무에게도 안 보인다).
 */
const AUTO_RETRY_MS = [0, 1200, 3000, 6000];

/** 판정의 자취. 값의 뜻은 위 표 — 화면에는 드러나지 않는다. */
type EntryFullscreenTrace = 'off' | 'already' | 'armed' | 'requested' | 'on' | 'denied';

function trace(value: EntryFullscreenTrace): void {
  document.documentElement.dataset.entryFullscreen = value;
}

export function useEntryFullscreen(): void {
  useEffect(() => {
    const display = readAppDisplay();
    if (!wantsEntryFullscreen(display)) {
      // **여기가 브라우저 탭이 가는 자리다.** 값이 `off` 라는 것 하나로 「기능이 없다」와
      // 「이 창에서는 켜지 않기로 했다」가 갈린다. 전체화면인 창(⌃⌘F)은 `installed` 이
      // 거짓이라 같은 가지로 오는데, 그쪽은 **이미 된 것**이라 값이 다르다.
      trace(display.fullscreen ? 'already' : 'off');
      return;
    }
    // 이미 전체화면인 창(⌃⌘F)에서는 할 일이 없다. `wantsEntryFullscreen` 이 이미
    // 거짓이지만, 전체화면 API 로 들어와 있는 경우(문서 안에서 한 번 더 마운트)도 뺀다.
    if (document.fullscreenElement !== null) {
      trace('already');
      return;
    }
    const controller = new AbortController();
    let timer = 0;
    let autoTimer = 0;
    /**
     * 한 번 물어본다. **`?.` 가 사슬 전체를 끊는다** — 전체화면 API 가 없는 표면에서는
     * `then` 까지 통째로 건너뛴다(거기서는 판정이 이미 거짓이라 여기 오지도 않는다).
     */
    const ask = (onDenied: () => void) => {
      void document.documentElement.requestFullscreen?.().then(() => {
        // ★ **성공했을 때만 걷는다** (위 ★★). 그래야 시청자가 ESC 로 나간 뒤 다시
        //    끌어들이지 않으면서, 거절은 다음 조작에서 회복된다.
        controller.abort();
        window.clearTimeout(timer);
        window.clearTimeout(autoTimer);
        timer = 0;
        trace('on');
      }, onDenied);
    };

    // ① **제스처 없이** — 「자동 전체 화면」 권한이 있는 창에서만 성립한다(위 ★★★).
    //    없으면 조용히 거절되고, 그때 값이 `armed` 라 **무엇이 없는지**가 드러난다.
    let attempt = 0;
    const askAuto = () => {
      if (controller.signal.aborted) return;
      trace('requested');
      ask(() => {
        trace('armed');
        attempt += 1;
        // 전체화면에서 빠져나오는 전환 중이면 권한이 있어도 거절된다 — 위 ★.
        if (attempt < AUTO_RETRY_MS.length) {
          autoTimer = window.setTimeout(askAuto, AUTO_RETRY_MS[attempt]);
        }
      });
    };
    askAuto();

    // ② 권한이 없는 창의 길 — 첫 조작에서. ①이 이미 성공했으면 리스너가 없다.
    const enter = () => {
      // 요청 하나가 이미 예약돼 있으면 겹쳐 걸지 않는다 — 조작이 빠르면 요청이 여러 번
      // 나가고, 그중 하나가 성공한 뒤의 나머지는 **이미 전체화면인 문서에 다시 요청**한다.
      if (timer !== 0) return;
      trace('requested');
      timer = window.setTimeout(() => {
        timer = 0;
        ask(() => {
          // 거절이다. **시청자에게 말할 것이 없다** — ⌃⌘F 가 그대로 있고, 리스너는
          // 살아 있어 다음 조작에서 다시 해 본다.
          trace('denied');
        });
      }, ENTER_DELAY_MS);
    };
    for (const type of ['click', 'keydown'] as const) {
      window.addEventListener(type, enter, { signal: controller.signal });
    }
    return () => {
      controller.abort();
      window.clearTimeout(timer);
      window.clearTimeout(autoTimer);
    };
  }, []);
}
